import { isPlainObject, pick } from 'lodash'
import QueryString from 'qs'
import { fetchFormValues } from '../../utils/formUtils/forms'
import { PageListParams } from '../globalTypes'
import { apiClient } from '../client'
import { ENDPOINT_FILTERS, PROPERTY_VALUE_ENDPOINTS } from './formValuesConfig'
import { OpportunityListParams, PropertyCriteria, PropertyParams, PropertyPayload, PropertyValuesData } from './types'

export const getPropertyById = async (id: string, params: PropertyParams = {}) => {
    const response = await apiClient(`v1/property/${id}`, {
        params,
        paramsSerializer: (params: PropertyParams) => {
            const criteria = isPlainObject(params.criteria) ? params.criteria : {}
            const paramsWithValidCriteria = { ...params, criteria }
            return QueryString.stringify(paramsWithValidCriteria)
        },
    })

    return response.data
}

export const getProperties = async (payload: PageListParams<PropertyCriteria>) => {
    const response = await apiClient(`v1/property`, {
        params: payload,
        paramsSerializer: (params: PageListParams<PropertyCriteria>) => {
            const criteria = (isPlainObject(params.criteria) ? params.criteria : {}) as Record<string, any>

            const normalizedCriteria = {
                ...criteria,
                // owner is stored as object in Redux for AsyncSelectInput display — extract value (id) for request
                ...(criteria.owner?.value && { owner: criteria.owner.value }),
            }

            const paramsWithValidCriteria = { ...params, criteria: normalizedCriteria }
            return QueryString.stringify(paramsWithValidCriteria)
        },
    })

    return response.data
}

export const getOpportunityById = async (params: OpportunityListParams) => {
    const { id, url, limit, order, page, entityType } = params
    const key = Object.keys(order)[0]
    const paramsLink = `criteria[${entityType}][0]=${id}&limit=${limit}&order[${key}]=${order[`${key}`]}&page=${page}`
    const paramsEncoded = QueryString.stringify(QueryString.parse(paramsLink))
    const apiUrl = `v1/${url}?${paramsEncoded}`

    const response = await apiClient(apiUrl)

    return response.data
}

export const createUpdateProperty = async (payload: PropertyPayload) => {
    const { isEdit, data } = payload
    const url = isEdit && 'id' in data ? `v1/property/${data.id}` : `v1/property`

    const response = await apiClient(url, {
        method: isEdit ? 'PUT' : 'POST',
        data: {
            ...data,
        },
    })

    return response.data
}

export const deletePropertyById = async (id: number) => {
    const response = await apiClient(`v1/property/${id}`, {
        method: 'DELETE',
    })

    return response.data
}

export const deleteProperties = async (ids: number[]) => {
    const response = await apiClient(`v1/property`, {
        method: 'DELETE',
        data: {
            ids,
        },
    })

    return response.data
}

export const cloneProperty = async (id: string, data: string[]) => {
    const response = await apiClient(`v1/property/duplicate/${id}`, {
        method: 'POST',
        data: data,
    })

    return response.data
}

export const fetchPropertyValues = (fields?: string[], formFilters?: Record<string, string | string[]>) => {
    const endpoints = fields ? pick(PROPERTY_VALUE_ENDPOINTS, fields) : PROPERTY_VALUE_ENDPOINTS

    return fetchFormValues<PropertyValuesData>(endpoints, formFilters, ENDPOINT_FILTERS)
}
